import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import SEO from '../../components/SEO';
import Slider from 'react-slick';
import RatingDisplay from '../../components/RatingDisplay';
import CertificationsManager from '../../components/CertificationsManager';
import { useRating } from '../../hooks/useRating';
import { useUser } from '../../context/UserContext';
import userDefault from '../../assets/img/user.svg';
import { ProfilePageSkeleton } from '../../components/loading-states';

export default function AsociadoPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [asociado, setAsociado] = useState<any>(null);
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [proyectos, setProyectos] = useState<any[]>([]);
  const [reseñas, setReseñas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ratingData, setRatingData] = useState<any>(null);
  const { calculateUserRating } = useRating();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) {
          setError('No se proporcionó un ID de asociado válido');
          setLoading(false);
          return;
        }

        const docRef = doc(db, 'asociado', id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError('El asociado no fue encontrado');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        setAsociado({ id: docSnap.id, ...data });

        // Cargar inmobiliaria
        if (data.inmobiliaria_doc_id) {
          const inmobiliariaDocRef = doc(db, 'inmobiliaria', data.inmobiliaria_doc_id);
          const inmobiliariaSnap = await getDoc(inmobiliariaDocRef);
          if (inmobiliariaSnap.exists()) {
            const inmData = inmobiliariaSnap.data();
            setInmobiliaria({ id: inmobiliariaSnap.id, ...inmData });

            // Buscar proyectos de la inmobiliaria
            const proyectosQuery = query(collection(db, 'proyectos'), where('inmobiliaria_doc_id', '==', data.inmobiliaria_doc_id));
            const proyectosSnap = await getDocs(proyectosQuery);
            setProyectos(proyectosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          }
        }

        // Cargar reseñas y calcular rating
        if (data.asociado_id) {
          try {
            const reseñasQuery = query(
              collection(db, 'calificacion'),
              where('perfil_id', '==', data.asociado_id),
              where('estatus', '==', 'aprobada')
            );
            const reseñasSnap = await getDocs(reseñasQuery);
            setReseñas(reseñasSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const ratingPromise = calculateUserRating(data.asociado_id, 'asociado');
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 5000)
            );

            const rating = await Promise.race([ratingPromise, timeoutPromise]);
            setRatingData(rating);
          } catch (error) {
            console.error('Error calculando rating:', error);
            setRatingData({
              calificacionFinal: data.calificacion || 0,
              breakdown: null,
              totalReviews: 0
            });
          }
        } else {
          setRatingData({
            calificacionFinal: data.calificacion || 0,
            breakdown: null,
            totalReviews: 0
          });
        }
      } catch (error) {
        console.error('Error al obtener datos:', error);
        setError('Ocurrió un error al cargar la información del asociado');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
  };

  if (loading) {
    return (
      <>
        {user ? <UserNav /> : <Navbar transparent={false} />}
        <ProfilePageSkeleton color="success" />
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  if (error || !asociado) {
    return (
      <>
        {user ? <UserNav /> : <Navbar transparent={false} />}
        <div className="container py-5">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error || 'Asociado no encontrado'}
          </div>
          <Link to="/asociados" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i>
            Volver a Asociados
          </Link>
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  const asociadoName = asociado?.nombre || 'Asociado Inmobiliario';
  const asociadoTitle = 'Asociado Certificado AMPI';
  const asociadoImage = asociado?.foto || 'https://ampirivieranayarit.com/wp-content/uploads/2025/03/mob-slide-1.jpg';
  const asociadoLocation = 'Riviera Nayarit';

  const asociadoSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": asociadoName,
    "jobTitle": asociadoTitle,
    "description": `${asociadoName} - ${asociadoTitle} certificado por AMPI Riviera Nayarit`,
    "url": `https://ampirivieranayarit.com/modulob/asociado-page/${id || ''}`,
    "image": asociadoImage,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": asociadoLocation,
      "addressCountry": "MX"
    },
    "memberOf": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit",
      "url": "https://ampirivieranayarit.com"
    }
  };

  // Generar link de WhatsApp
  const whatsappNumber = asociado.telefono
    ? asociado.telefono.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '')
    : null;
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hola ${asociado.nombre}, me gustaría obtener más información.`)}`
    : null;

  return (
    <>
      <SEO
        title={`${asociadoName} - ${asociadoTitle}`}
        description={`Perfil de ${asociadoName}, ${asociadoTitle} certificado en ${asociadoLocation}. Contacta con asociados verificados por AMPI Riviera Nayarit.`}
        path={`/modulob/asociado-page/${id || ''}`}
        image={asociadoImage}
        type="profile"
        schema={asociadoSchema}
      />
      {user ? <UserNav /> : <Navbar transparent={false} />}

      <Breadcrumb
        items={[
          { label: 'Inicio', path: '/' },
          { label: 'Asociados', path: '/asociados' },
          { label: asociado.nombre || 'Asociado', path: '' }
        ]}
      />

      {/* Hero del Asociado */}
      <section className="bg-success py-4">
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-center text-center text-md-start gap-4">
            <img
              src={asociado.foto || userDefault}
              alt={`Foto de ${asociado.nombre}`}
              className="rounded-circle border border-4 border-white shadow"
              width="150"
              height="150"
              style={{ objectFit: 'cover', flexShrink: 0 }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = userDefault;
              }}
            />
            <div>
              <h2 className="text-white mb-1">{asociado.nombre}</h2>
              <p className="text-white-50 mb-2">
                <i className="fas fa-certificate me-2"></i>
                Asociado Certificado AMPI
              </p>
              {asociado.anos_experiencia > 0 && (
                <span className="badge bg-light text-success me-2">
                  <i className="fas fa-briefcase me-1"></i>
                  {asociado.anos_experiencia} años de experiencia
                </span>
              )}
              {inmobiliaria && (
                <Link
                  to={`/agency-page/${inmobiliaria.id}`}
                  className="badge bg-light text-success text-decoration-none me-2"
                >
                  <i className="fas fa-building me-1"></i>
                  {inmobiliaria.nombre}
                </Link>
              )}

              {/* Redes Sociales */}
              {(asociado.redes?.facebook || asociado.redes?.instagram) && (
                <div className="d-flex gap-2 mt-3 justify-content-center justify-content-md-start">
                  {asociado.redes?.facebook && (
                    <a
                      href={asociado.redes.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-light"
                    >
                      <i className="fab fa-facebook text-primary"></i>
                    </a>
                  )}
                  {asociado.redes?.instagram && (
                    <a
                      href={asociado.redes.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-light"
                    >
                      <i className="fab fa-instagram text-danger"></i>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="gray-simple">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12">
              {/* Biografía */}
              {asociado.biografia && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-info-circle text-success me-2"></i>
                      Acerca de mí
                    </h5>
                    <p className="text-muted mb-0">{asociado.biografia}</p>
                  </div>
                </div>
              )}

              {/* Calificación Profesional */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-star text-warning me-2"></i>
                    {t('agents.professionalRating')}
                  </h5>
                  {ratingData ? (
                    <RatingDisplay
                      score={ratingData.calificacionFinal}
                      showBreakdown={false}
                      size="lg"
                      className="mb-3"
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center py-4">
                      <div className="spinner-border text-warning me-3" role="status">
                        <span className="visually-hidden">{t('agents.calculating')}</span>
                      </div>
                      <span className="text-muted">{t('agents.calculating')}</span>
                    </div>
                  )}

                  {reseñas.length > 0 && (
                    <div className="alert alert-info mb-0">
                      <i className="fas fa-comments me-2"></i>
                      {t('agents.basedOn')} <strong>{reseñas.length}</strong> {t('agents.reviews')}
                    </div>
                  )}
                </div>
              </div>

              {/* Reseñas */}
              {reseñas.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-comments text-success me-2"></i>
                      Reseñas
                    </h5>
                    <Slider {...sliderSettings}>
                      {reseñas.map((r, index) => (
                        <div key={index} className="p-2">
                          <div className="card p-4 text-center shadow-sm border">
                            <p className="mb-3 fst-italic">"{r.reseña}"</p>
                            <div className="mb-0">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i key={star} className={`fas fa-star ${star <= r.estrellas ? 'text-warning' : 'text-muted'}`} />
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              )}

              {/* Certificaciones */}
              {asociado?.certificaciones && asociado.certificaciones.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fa-solid fa-certificate text-success me-2"></i>
                      Certificaciones Profesionales
                    </h5>
                    <CertificationsManager
                      userId={id || ''}
                      userType="asociado"
                      certificaciones={asociado.certificaciones}
                      readOnly={true}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              {/* Contacto */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-address-book text-success me-2"></i>
                    Contacto
                  </h5>

                  {/* Teléfono */}
                  {asociado.telefono && (
                    <div className="d-flex align-items-center mb-3">
                      <i className="fas fa-phone text-success me-3"></i>
                      <div>
                        <small className="text-muted d-block">Teléfono</small>
                        <a href={`tel:${asociado.telefono}`} className="fw-bold text-decoration-none">
                          {asociado.telefono}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {asociado.correo && (
                    <div className="d-flex align-items-center mb-4">
                      <i className="fas fa-envelope text-success me-3"></i>
                      <div className="text-truncate">
                        <small className="text-muted d-block">Email</small>
                        <a href={`mailto:${asociado.correo}`} className="fw-bold text-decoration-none">
                          {asociado.correo}
                        </a>
                      </div>
                    </div>
                  )}

                  <hr />

                  {/* Botones de acción */}
                  <div className="d-grid gap-2">
                    {whatsappUrl && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success"
                      >
                        <i className="fab fa-whatsapp me-2"></i>
                        WhatsApp
                      </a>
                    )}
                    {asociado.correo && (
                      <a href={`mailto:${asociado.correo}`} className="btn btn-primary">
                        <i className="fas fa-envelope me-2"></i>
                        Enviar Email
                      </a>
                    )}
                    {asociado.telefono && (
                      <a href={`tel:${asociado.telefono}`} className="btn btn-outline-primary">
                        <i className="fas fa-phone me-2"></i>
                        Llamar
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Inmobiliaria */}
              {inmobiliaria && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-building text-success me-2"></i>
                      Inmobiliaria
                    </h5>
                    <div className="d-flex align-items-center">
                      {inmobiliaria.logo ? (
                        <img
                          src={inmobiliaria.logo}
                          alt={inmobiliaria.nombre}
                          className="rounded me-3"
                          width="50"
                          height="50"
                          style={{ objectFit: 'contain' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div
                          className="bg-success text-white rounded d-flex align-items-center justify-content-center me-3"
                          style={{ width: '50px', height: '50px', flexShrink: 0 }}
                        >
                          <i className="fas fa-building"></i>
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <Link to={`/agency-page/${inmobiliaria.id}`} className="fw-bold text-decoration-none d-block">
                          {inmobiliaria.nombre}
                        </Link>
                        {inmobiliaria.telefono && (
                          <small className="text-muted">{inmobiliaria.telefono}</small>
                        )}
                      </div>
                    </div>
                    <Link
                      to={`/agency-page/${inmobiliaria.id}`}
                      className="btn btn-outline-success btn-sm w-100 mt-3"
                    >
                      <i className="fas fa-eye me-1"></i>
                      Ver Inmobiliaria
                    </Link>
                  </div>
                </div>
              )}

              {/* Proyectos */}
              {proyectos.length > 0 && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-project-diagram text-success me-2"></i>
                      Proyectos ({proyectos.length})
                    </h5>
                    <ul className="list-unstyled mb-0">
                      {proyectos.map((proy, idx) => (
                        <li key={proy.id} className={idx < proyectos.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}>
                          <Link to={`/view-project/${proy.id}`} className="text-decoration-none fw-bold">
                            <i className="fas fa-building me-2 text-muted"></i>
                            {proy.nombre}
                          </Link>
                          <small className="d-block text-muted mt-1 ms-4">
                            {proy.propiedad_id?.length || 0} propiedades
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
